/**
 * Phone and desktop view of the saved assistant thread.
 * Used to check the chat on a DEV bundle. It does not call the
 * assistant, Stripe, or a database. Live hosts do not mount this.
 */
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useTheme } from '../ThemeContext';
import AppTabBar from './AppTabBar';
import AssistantThread from './AssistantThread';
import { AgentAiNotice, HomeownerAiNotice } from './AiAssistantNotice';
import QuestionsLeftNote from './QuestionsLeftNote';
import { AGENT_MOBILE_TABS, HOMEOWNER_MOBILE_TABS } from '../shared/mobileTabs';
import { sampleThread } from '../shared/assistantThread';

const BRAND = '#59c4c4';
const KEYBOARD_HEIGHT = '336px';

function previewRole(search) {
  const role = new URLSearchParams(search || '').get('role');
  return role === 'agent' ? 'agent' : 'homeowner';
}

function previewFlag(search, name) {
  return new URLSearchParams(search || '').get(name) === '1';
}

function longThread(now) {
  const today = now instanceof Date ? now : new Date();
  const older = [];
  for (let index = 0; index < 14; index += 1) {
    const at = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (16 - index), 9, 5);
    older.push({
      id: `older-${index}:user`,
      role: 'user',
      content: `Older question ${index + 1}. How do I take care of this part of the house?`,
      timestamp: at,
    });
    older.push({
      id: `older-${index}:assistant`,
      role: 'assistant',
      content: `Older answer ${index + 1}. Check it once a season. If you smell gas or see sparks, stop and call a pro.`,
      timestamp: new Date(at.getTime() + 60000),
    });
  }
  return older.concat(sampleThread(today));
}

export default function AiChatPreview() {
  const { colors, isDark } = useTheme();
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const role = previewRole(search);
  const keyboard = previewFlag(search, 'keyboard');
  const sponsorless = role !== 'agent' && previewFlag(search, 'sponsorless');
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 768);
  const wordmark = isDark
    ? `${process.env.PUBLIC_URL || ''}/images/logo/pacarion-logo-dark.png`
    : `${process.env.PUBLIC_URL || ''}/images/logo/pacarion-logo-light.png`;
  const tabs = role === 'agent' ? AGENT_MOBILE_TABS : HOMEOWNER_MOBILE_TABS;
  const [messages] = useState(() => longThread(new Date()));
  const [text, setText] = useState('');

  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useLayoutEffect(() => {
    if (!keyboard) return undefined;
    const root = document.documentElement;
    root.dataset.keyboardPreview = '1';
    root.classList.add('keyboard-open');
    root.style.setProperty('--keyboard-inset', KEYBOARD_HEIGHT);
    return () => {
      delete root.dataset.keyboardPreview;
      root.classList.remove('keyboard-open');
      root.style.removeProperty('--keyboard-inset');
    };
  }, [keyboard]);

  return (
    <div
      data-preview="ai-chat"
      data-preview-role={role}
      data-preview-ready="true"
      className="app-shell assistant-open"
      style={{
        background: colors.background,
        color: colors.text,
        boxSizing: 'border-box',
      }}
    >
      <header
        className={wide ? undefined : 'mobile-top-bar'}
        style={{
          background: colors.surface,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: wide ? 'flex-start' : 'center',
          flex: '0 0 auto',
          minHeight: wide ? undefined : 'calc(env(safe-area-inset-top, 0px) + 44px)',
          padding: wide ? '0.75rem 1.5rem' : undefined,
          paddingTop: wide ? undefined : 'env(safe-area-inset-top, 0px)',
          boxSizing: 'border-box',
          position: wide ? 'relative' : 'fixed',
          top: wide ? undefined : 0,
          left: wide ? undefined : 0,
          right: wide ? undefined : 0,
          zIndex: 999,
        }}
      >
        <img className="top-bar-wordmark" src={wordmark} alt="Pacarion" style={{ height: wide ? '40px' : '33px', width: 'auto' }} />
      </header>
      <main
        className={`app-scroll assistant-pane${wide ? '' : ' mobile-under-header mobile-content'}`}
        style={{
          width: '100%',
          maxWidth: wide ? '760px' : 'none',
          margin: wide ? '0 auto' : undefined,
          padding: wide ? '1.25rem 1.5rem 1rem' : undefined,
          boxSizing: 'border-box',
          background: colors.background,
        }}
      >
        <AssistantThread
          messages={messages}
          colors={colors}
          isMobile={!wide}
          intro={role === 'agent' ? 'Ask about a home you sponsor. Your chats stay here.' : undefined}
          brandColor={BRAND}
          onExample={setText}
          renderExtra={role === 'homeowner' ? (msg) => (
            msg.suggestTask && msg.taskSuggestion ? (
              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${colors.border}` }}>
                <button
                  type="button"
                  style={{
                    width: '100%',
                    minHeight: '44px',
                    padding: '0.75rem',
                    background: BRAND,
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: 600,
                  }}
                >
                  + Create Task for This
                </button>
              </div>
            ) : null
          ) : undefined}
        >
          <textarea
            data-assistant-input="true"
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Type your message..."
            style={{
              width: '100%',
              minHeight: '44px',
              borderRadius: wide ? '0.5rem' : '1.25rem',
              border: `2px solid ${BRAND}`,
              padding: '0.75rem 1rem',
              fontSize: '1rem',
              boxSizing: 'border-box',
              background: colors.surface,
              color: colors.text,
              resize: 'none',
            }}
          />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button
              type="button"
              style={{
                minHeight: '44px',
                padding: '0.75rem 1.25rem',
                borderRadius: wide ? '0.5rem' : '1.25rem',
                border: `2px solid ${colors.border}`,
                background: colors.surfaceAlt,
                color: colors.text,
                fontWeight: 650,
              }}
            >
              Attach
            </button>
            <button
              type="button"
              style={{
                minHeight: '44px',
                minWidth: '80px',
                padding: '0.75rem 1.25rem',
                borderRadius: wide ? '0.5rem' : '1.25rem',
                border: 'none',
                background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
                color: '#ffffff',
                fontWeight: 700,
              }}
            >
              Send
            </button>
          </div>
          {role === 'agent'
            ? <AgentAiNotice color={colors.textSecondary} />
            : <HomeownerAiNotice color={colors.textSecondary} sponsorless={sponsorless} />}
          <QuestionsLeftNote used={8} limit={20} color={colors.textSecondary} />
        </AssistantThread>
      </main>
      {wide ? null : (
        <AppTabBar
          tabs={tabs}
          activeId="assistant"
          onSelect={() => {}}
          colors={colors}
          isDark={isDark}
        />
      )}
      {keyboard ? (
        <div
          data-preview-keyboard="true"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            height: KEYBOARD_HEIGHT,
            zIndex: 1100,
            boxSizing: 'border-box',
            background: '#d1d5db',
            borderTop: '1px solid #9ca3af',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#374151',
            fontSize: '1rem',
            fontWeight: 650,
          }}
        >
          Keyboard
        </div>
      ) : null}
    </div>
  );
}
